import { useEffect } from "react"
import { create } from "zustand"
import { persist } from "zustand/middleware"

const STORAGE_KEY = "activePortalInstanceId"

interface ActivePortalInstanceStore {
	/**
	 * The portal instance the user has selected for the dashboard's portal-scoped
	 * surfaces (docs list, changelog list, roadmap filter). `null` means single-
	 * portal mode (workspace has 0 or 1 instances, so no filtering applies).
	 */
	activePortalInstanceId: string | null
	setActivePortalInstanceId: (id: string | null) => void
}

/**
 * Persists to localStorage so the choice survives reloads and is shared across
 * tabs — matching the multi-language switcher's behavior.
 */
export const useActivePortalInstanceStore = create<ActivePortalInstanceStore>()(
	persist(
		(set) => ({
			activePortalInstanceId: null,
			setActivePortalInstanceId: (id) => set({ activePortalInstanceId: id }),
		}),
		{ name: STORAGE_KEY },
	),
)

/**
 * Snapshot accessor for use inside event handlers and async callbacks where
 * subscribing to re-renders is unnecessary. Returns `null` in single-portal
 * mode (workspace has 0 or 1 instances).
 */
export const getActivePortalInstanceId = () =>
	useActivePortalInstanceStore.getState().activePortalInstanceId

// Live cross-tab sync: when another tab flips the portal, `storage` fires here
// and we re-hydrate so the in-memory store matches the persisted value.
if (typeof window !== "undefined") {
	window.addEventListener("storage", (event) => {
		if (event.key === STORAGE_KEY) {
			void useActivePortalInstanceStore.persist.rehydrate()
		}
	})
}

/**
 * Hook that returns the active portal-instance id and a setter. `null` means
 * the implicit Root portal (unprefixed URL). The hook also guards against
 * stale ids — if the persisted id points to a portal that no longer exists,
 * it falls back to Root.
 */
export function useActivePortalInstance(
	portalInstances: ReadonlyArray<{ id: string }>,
): [string | null, (id: string | null) => void] {
	const { activePortalInstanceId, setActivePortalInstanceId } =
		useActivePortalInstanceStore()

	useEffect(() => {
		// Null = Root, which is always valid. Reset only if a non-null id no
		// longer corresponds to a real portal (e.g. it was deleted).
		if (activePortalInstanceId === null) return
		const stillExists = portalInstances.some(
			(i) => i.id === activePortalInstanceId,
		)
		if (!stillExists) setActivePortalInstanceId(null)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [portalInstances.map((i) => i.id).join(",")])

	return [activePortalInstanceId, setActivePortalInstanceId]
}
